'use client';

import {
  Alert,
  Box,
  CardContent,
  Divider,
  FormControl,
  FormLabel,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Provider } from '@repo/api-gw-sdk';
import { useEffect, useMemo, useState } from 'react';

import { extractErrorMessage } from '@/app/(blank-layout-pages)/(authenticated)/onboarding/errorHandling';
import {
  EncryptionComponent,
  getEncryptionKey,
} from '@/components/encryption/encryptionComponent';
import { FormTextField } from '@/components/form/formTextField';
import { DocumentdbParameterGroupSelect } from '@/components/selectors/documentdbParameterGroupSelect';
import { RDSSubnetGroupSelect } from '@/components/selectors/rdsSubnetGroupSelect';
import { BrowserGrid } from '@/components/shared/browserGrid';
import { SnapshotToolbar } from '@/components/snapshots/snapshotToolbar';
import { TagsSection } from '@/components/tags/tagsSection';
import type { StepProps } from '@/components/wizard/StepProps';
import { useDAL } from '@/contexts/useDAL';

import { EonTagRemark } from './eonTagRemark';

import { StepContainer } from '../../wizard/StepContainer';
import type { RestoreDocumentdbState } from '../restoreDocumentdbWizard';

const requiredValidation = {
  validateInput: (v: string) => !!v?.trim(),
  errorMessage: () => 'Required' as const,
};

export const DocumentdbConfiguration = (
  props: StepProps<RestoreDocumentdbState>
) => {
  const dal = useDAL();

  const [error, setError] = useState<string | undefined>();
  const [isRestoring, setIsRestoring] = useState(false);

  const hasSourceAccount = !!props.wizardState.restoreAccount;

  const docdbProps = props.wizardState.snapshot.documentDbSnapshotProperties;
  const backupVaultInfo = useMemo(() => {
    if (!docdbProps) return undefined;
    return {
      cloudProvider: Provider.Aws,
      region: docdbProps.region || props.wizardState.initialRegionName,
    };
  }, [docdbProps, props.wizardState.initialRegionName]);

  useEffect(() => {
    props.setWizardState((state) => ({
      ...state,
      vpcSecurityGroupIds: [],
      regionName: state.initialRegionName,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.wizardState.restoreAccount]);

  useEffect(() => {
    if (
      !props.wizardState.restoreAccount &&
      props.wizardState.accounts?.length
    ) {
      props.setWizardState((state) => ({
        ...state,
        restoreAccount: state.accounts?.[0],
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.wizardState.accounts]);

  const canGoNext = () =>
    hasSourceAccount &&
    !!props.wizardState.clusterIdentifier?.trim() &&
    !!props.wizardState.subnetGroupName &&
    !isRestoring;

  const onNextClick = async () => {
    if (!props.wizardState.restoreAccount || !props.wizardState.regionName) {
      return;
    }

    setIsRestoring(true);
    setError(undefined);

    try {
      await dal.restore.documentdb.restore(
        props.wizardState.resourceId,
        props.wizardState.snapshot.id,
        {
          restoreAccountId: props.wizardState.restoreAccount.id,
          destination: {
            region: props.wizardState.regionName,
            clusterIdentifier: props.wizardState.clusterIdentifier!,
            port: props.wizardState.port,
            subnetGroupName: props.wizardState.subnetGroupName,
            encryptionKey: getEncryptionKey(props.wizardState.encryption),
            tags: props.wizardState.tags,
          },
        }
      );
      await props.finish();
    } catch (e) {
      setError(await extractErrorMessage(e));
    } finally {
      setIsRestoring(false);
    }
  };

  return (
    <StepContainer
      sx={{ padding: '0' }}
      hideStepper
      navigationComponent={
        error ? (
          <Stack
            alignItems='center'
            direction='row'
            sx={{ color: 'var(--mui-palette-error-main)' }}
          >
            <i className='ri-error-warning-line mr-[8px] text-[18px]' />
            {error}
          </Stack>
        ) : (
          <EonTagRemark />
        )
      }
      stepperLabels={[]}
      stepperIndex={0}
      nextButtonText='Restore'
      nextButtonLoadingText='Restoring...'
      onBackClick={props.back}
      canGoNext={canGoNext}
      onNextClick={onNextClick}
      toolbarComponent={
        <SnapshotToolbar
          pointInTime={props.wizardState.snapshot.pointInTime}
          vaultId={props.wizardState.snapshot.vaultId}
          accessDeniedItemsCount={
            props.wizardState.snapshot.accessDeniedItemsCount
          }
          supportPiTR={props.wizardState.snapshot.supportPiTR}
          backupVaultInfo={backupVaultInfo}
        />
      }
    >
      <Box className='mx-[40px] my-[24px]'>
        {!hasSourceAccount && (
          <Alert severity='info' className='mb-[20px]'>
            <Typography variant='body2'>
              {props.wizardState.emptyMessage}
            </Typography>
          </Alert>
        )}

        <Box className='mt-[20px]'>
          <BrowserGrid columns={2} gap='24px'>
            <FormTextField
              title='Cluster identifier'
              dataTestId='clusterIdentifierTextField'
              validation={requiredValidation}
              placeholder='Enter cluster identifier'
              value={props.wizardState.clusterIdentifier || ''}
              onChange={(e) =>
                props.setWizardState((state) => ({
                  ...state,
                  clusterIdentifier: e.target.value,
                }))
              }
            />

            <DocumentdbParameterGroupSelect
              accountId={props.wizardState.restoreAccount?.id}
              regionName={props.wizardState.regionName}
              parameterGroupName={props.wizardState.parameterGroupName}
              onChange={(parameterGroupName) =>
                props.setWizardState((state) => ({
                  ...state,
                  parameterGroupName,
                }))
              }
            />

            <RDSSubnetGroupSelect
              accountId={props.wizardState.restoreAccount?.id}
              regionName={props.wizardState.regionName}
              rdsSubnetGroupName={props.wizardState.subnetGroupName}
              onChange={(subnetGroupName) =>
                props.setWizardState((state) => ({
                  ...state,
                  subnetGroupName,
                }))
              }
            />

            <FormControl size='small'>
              <FormLabel>Port</FormLabel>
              <TextField
                data-testid='portTextField'
                size='small'
                type='number'
                placeholder='27017'
                value={props.wizardState.port ?? ''}
                onChange={(e) =>
                  props.setWizardState((state) => ({
                    ...state,
                    port: e.target.value ? parseInt(e.target.value) : undefined,
                  }))
                }
              />
            </FormControl>
          </BrowserGrid>
        </Box>
      </Box>

      {props.wizardState.regionName && hasSourceAccount && (
        <>
          <Divider />
          <CardContent className='px-0'>
            <EncryptionComponent
              accountId={props.wizardState.restoreAccount!.id}
              regionName={props.wizardState.regionName}
              onChange={(encryption) =>
                props.setWizardState((state) => ({ ...state, encryption }))
              }
              encryption={props.wizardState.encryption}
            />
          </CardContent>
        </>
      )}

      <Divider />

      <CardContent>
        <Stack className='mx-[16px]' gap='24px'>
          <TagsSection
            tags={props.wizardState.tags}
            initialTags={props.wizardState.initialTags}
            keepOriginalTags={props.wizardState.keepOriginalTags}
            onChange={(tags: Record<string, string>) =>
              props.setWizardState((state) => ({ ...state, tags }))
            }
            setKeepOriginalTags={(keepOriginalTags: boolean) =>
              props.setWizardState((state) => ({
                ...state,
                keepOriginalTags,
              }))
            }
          />
        </Stack>
      </CardContent>
    </StepContainer>
  );
};
